// Check tiles and the small shared pieces of the platform health page. On a check tile's FACE: the state as a WORD,
// the check name, the monitor it came from, the reason, the measured time and its age (plus failing-since for a
// measured problem and the legacy badge). Behind its own "details" toggle: the raw reading, the coverage denominator
// and notes, the monitor's scope note, class, severity and run id. Nothing here decides a state: look() maps the
// server's, and can only downgrade an answer that contradicts itself. Every field drawn is one the server redacted.
import type { HealthGridLine, HealthMonitor, HealthTile } from "./types";
import { checkKey, clip, coverageText, fmtAgeH, fmtUtc, lineLook, look, sinceText, valueText, type Look } from "./present";

const VALUE_CLIP = 400;

export function LegacyBadge() {
  return (
    <span className="ph-legacy" data-testid="legacy-badge">
      legacy record - this monitor cannot report UNKNOWN
    </span>
  );
}

export function StateWord({ lk, word }: { lk: Look; word?: string }) {
  return (
    <span className="ph-word" data-kind={lk.kind}>
      {word ?? lk.word}
    </span>
  );
}

/** A status line's word (schedule / ran / complete / moved), with its kind as a glyph - never a tile's state. */
export function LineWord({ l }: { l: HealthGridLine }) {
  const lk = lineLook(l);
  return (
    <span className="ph-lw" data-line-kind={lk.kind}>
      {lk.word}
    </span>
  );
}

export function Contradiction({ lk }: { lk: { contradiction: string | null } }) {
  return lk.contradiction ? (
    <p className="ph-contra" data-testid="contradiction">
      {lk.contradiction}
    </p>
  ) : null;
}

/** A check tile's face shows at most this much of its reason; the full text is behind the tile's "details" toggle. */
export const REASON_FACE_CLIP = 300;

const reasonText = (reason: string | null) => (typeof reason === "string" ? reason.trim() : "");
const isLongReason = (reason: string | null, clipAt: number | undefined) => clipAt !== undefined && reasonText(reason).length > clipAt;

/** The reason. With `clipAt`, a longer one is clipped with an ellipsis and says where the full text is (the caller puts
 *  it behind its details toggle - it is never dropped). */
export function Reason({ lk, reason, clipAt }: { lk: Look; reason: string | null; clipAt?: number }) {
  const text = reasonText(reason);
  const long = isLongReason(reason, clipAt);
  const shown = long ? clip(text, clipAt as number) : text;
  const more = long ? <span className="ph-hint"> (full reason under details)</span> : null;
  if (lk.kind === "fine") {
    return text ? (
      <p className="ph-diag" data-clipped={long ? "true" : undefined}>
        {shown}
        {more}
      </p>
    ) : null;
  }
  return (
    <p className="ph-reason" data-testid="reason" data-clipped={long ? "true" : undefined}>
      {shown || "the server gave no reason"}
      {more}
    </p>
  );
}

/** The reading, verbatim. Behind the details toggle; a very long one is clipped with the full text one more click away. */
function Reading({ value }: { value: unknown }) {
  const text = valueText(value);
  if (text.length <= VALUE_CLIP) return <span className="ph-value">{text}</span>;
  return (
    <details className="ph-value-long">
      <summary className="ph-value">{clip(text, VALUE_CLIP)}</summary>
      <span className="ph-value">{text}</span>
    </details>
  );
}

export const timeLabel = (state: string) => (state === "MEASURED" ? "measured" : "last evidence");

/** "from <monitor>" on a check. `from` undefined = do not say (store self); null = the answer does not identify
 *  exactly one monitor, and the tile says so rather than guess. R5 E3: a per-pipeline reading moved off a NOT_APPLICABLE
 *  asset is no monitor tile - it says "reading of <monitor>/<check>" (its monitor named by the answer's own tile.monitor). */
function From({ from, reading = false, check }: { from: HealthMonitor | null | undefined; reading?: boolean; check: string }) {
  if (from === undefined) return null;
  return (
    <span className="ph-from" data-testid="check-from" data-reading={reading ? "true" : undefined}>
      {from ? (reading ? `reading of ${from.id}/${check}` : `from ${from.id}`) : "monitor not identified by the answer"}
    </span>
  );
}

export function CheckTile({
  tile,
  from,
  reading = false,
  where = null,
  coverageOverride,
  keyOverride
}: {
  tile: HealthTile;
  from?: HealthMonitor | null;
  /** R5 E3: a per-pipeline reading moved off a NOT_APPLICABLE asset (drawn "reading of <monitor>/<check>") */
  reading?: boolean;
  where?: string | null;
  coverageOverride?: string;
  /** the identity written to data-check (tests count every check drawn exactly once) */
  keyOverride?: string;
}) {
  const lk = look(tile);
  const quiet = lk.kind === "fine" || lk.kind === "info" || lk.kind === "na" || lk.kind === "recovered";
  const cov = coverageOverride ?? coverageText(tile.coverage);
  const stated = coverageOverride !== undefined || (tile.coverage !== null && (typeof tile.coverage.measured === "number" || typeof tile.coverage.expected === "number"));
  return (
    <div
      className={`ph-check ph-k-${lk.kind}${quiet ? " ph-check-quiet" : ""}`}
      data-kind={lk.kind}
      data-state={tile.state}
      data-check={keyOverride ?? checkKey(from, tile)}
      data-testid="check-tile"
    >
      <div className="ph-check-head">
        <StateWord lk={lk} />
        <code className="ph-check-name">{tile.check}</code>
        <From from={from} reading={reading} check={tile.check} />
      </div>
      <Contradiction lk={lk} />
      <Reason lk={lk} reason={tile.reason} clipAt={REASON_FACE_CLIP} />
      <p className="ph-check-time">
        {timeLabel(tile.state)} {fmtUtc(tile.measured_at_utc)} · {fmtAgeH(tile.age_h)}
        {stated ? <> · {cov}</> : null}
        {lk.kind === "problem" ? <> · {sinceText(tile.failing_since_utc, tile.failing_since_is_floor)}</> : null}
      </p>
      {tile.legacy ? <LegacyBadge /> : null}
      <details className="ph-tile-details" data-testid="check-details">
        <summary>details</summary>
        <dl className="ph-facts">
          {isLongReason(tile.reason, REASON_FACE_CLIP) ? (
            <>
              <dt>full reason</dt>
              <dd data-testid="reason-full">{reasonText(tile.reason)}</dd>
            </>
          ) : null}
          <dt>reading</dt>
          <dd>
            <Reading value={tile.value} />
          </dd>
          <dt>coverage</dt>
          <dd data-testid="coverage-text">{cov}</dd>
          {tile.coverage?.note ? (
            <>
              <dt>scope</dt>
              <dd>{tile.coverage.note}</dd>
            </>
          ) : null}
          {from?.coverage_note ? (
            <>
              <dt>monitor scope</dt>
              <dd>{from.coverage_note}</dd>
            </>
          ) : null}
          {tile.unknown_cause ? (
            <>
              <dt>why unknown</dt>
              <dd>{tile.unknown_cause.label}</dd>
            </>
          ) : null}
          {tile.failure_class ? (
            <>
              <dt>class</dt>
              <dd>{tile.failure_class}</dd>
            </>
          ) : null}
          {tile.severity ? (
            <>
              <dt>severity</dt>
              <dd>{tile.severity}</dd>
            </>
          ) : null}
          {tile.run_id ? (
            <>
              <dt>run</dt>
              <dd>
                <code>{tile.run_id}</code>
              </dd>
            </>
          ) : null}
          {where ? (
            <>
              <dt>where</dt>
              <dd data-testid="check-where">{where}</dd>
            </>
          ) : null}
        </dl>
      </details>
    </div>
  );
}

/** The first-broken-thing role, in words (never a colour). */
export function RoleBadge({ role, collapsedUnder, labelOf }: { role: string; collapsedUnder: string[]; labelOf: (id: string) => string }) {
  switch (role) {
    case "primary":
      return (
        <span className="ph-role" data-testid="role-primary">
          primary - the first broken thing in its chain
        </span>
      );
    case "possibly_consequent":
      return (
        <span className="ph-role" data-testid="role-possibly-consequent">
          possibly consequent - upstream unknown
        </span>
      );
    case "consequent":
      return (
        <span className="ph-role" data-testid="role-consequent">
          consequent of {collapsedUnder.map(labelOf).join(", ")}
        </span>
      );
    default:
      return null;
  }
}

/** A monitor's schedule, in words. */
export function triggerText(m: HealthMonitor): string {
  if (m.trigger === "push") return "push-triggered - no schedule, so its absence cannot be judged";
  if (m.trigger === "schedule") {
    const cron = m.cron ? ` ${m.cron}` : "";
    return typeof m.cadence_h === "number" ? `scheduled${cron} · cadence ${m.cadence_h}h` : `scheduled${cron} · cadence unknown`;
  }
  return "no schedule or trigger it can be judged by";
}

export function writeText(m: HealthMonitor): string {
  if (!m.publishes) return "does not publish to the state store";
  return m.last_write_utc ? `last write ${fmtUtc(m.last_write_utc)} · ${fmtAgeH(m.age_h)}` : "no write ever observed";
}
